import {
  BlockDomain,
  CRICKET_BOWLING_COACH_PROFILE_ROLE,
  ProfileRoleType,
  TeamDetail,
} from '@strength/domain';
import { DateTime } from 'luxon';
import { CSSProperties } from 'react';
import { v4 } from 'uuid';
import { z } from 'zod';
import { Block, TrackPreview, TrackRow } from './types';
import { BlockForm } from './forms/block-form-schema';

export const TRACK_HEIGHT = 42;
export const DATE_HEADER_HEIGHT = 64;
export const SIDE_PANEL_WIDTH = 300;
export const BOTTOM_PADDING = 68;

export function stylesForIndex(index: number): CSSProperties {
  return {
    height: TRACK_HEIGHT,
    top: TRACK_HEIGHT * index,
  };
}

export function stylesForBlock(index: number): CSSProperties {
  return {
    height: 34,
    top: TRACK_HEIGHT * index + 4,
  };
}

export function calculateFirstBlockDate(
  blocks: Block[],
  tracks: TrackPreview[],
): DateTime {
  const trackIds = tracks.map((t) => t.id);
  if (blocks.length === 0) {
    return DateTime.now();
  } else {
    const firstBlock = blocks
      .filter((tb) => trackIds.includes(tb.trackId))
      .sort((a, b) => {
        return (
          DateTime.fromISO(a.startDate).toMillis() -
          DateTime.fromISO(b.startDate).toMillis()
        );
      })[0];
    const date = DateTime.fromISO(firstBlock?.startDate ?? '');
    return date.isValid ? date : DateTime.now();
  }
}

type CreateBlockInput =
  | z.infer<typeof BlockDomain.Create.shape.input>
  | z.infer<typeof BlockDomain.Copy.shape.input>;

/**
 * The end date a block form saves for a given start date and schedule: the
 * start plus one full cycle per block-layout entry, minus a day. Shared by
 * the create/edit inputs and the drawer's Current vs New preview so the
 * preview always shows exactly what a save will write.
 */
export const getBlockEndDateFromForm = (
  startDate: string,
  blockLayout: number[],
): string => {
  const _startDate = DateTime.fromISO(startDate);
  if (!_startDate.isValid) {
    // shouldn't ever happen but is needed so endDate is the correct type
    throw new Error('Invalid start date');
  }
  const cycleLengthInDays = BlockDomain.DEFAULT_CYCLE_LENGTH_IN_DAYS;
  return _startDate
    .plus({ days: cycleLengthInDays * blockLayout.length - 1 })
    .toISODate();
};

export const createBlockInput = (
  teamDetail: TeamDetail,
  {
    name,
    focuses,
    blockType,
    trainingMaxRatio,
    startDate,
    blockLayout,
    autoSchedule,
    allowFlexWorkouts,
    trackId,
  }: BlockForm,
): CreateBlockInput => {
  const blockFocuses = focuses.map((f, index) => ({
    id: v4(),
    focusId: f.id,
    sortOrder: index,
  }));

  const _startDate = DateTime.fromISO(startDate);
  if (!_startDate.isValid) {
    // shouldn't ever happen but is needed so endDate is the correct type
    throw new Error('Invalid start date');
  }

  return {
    id: v4(),
    name,
    focuses: blockFocuses,
    blockType,
    startDate,
    endDate: getBlockEndDateFromForm(startDate, blockLayout),
    trainingMaxRatio,
    cycleLabel: teamDetail.settings.defaultCycleLabel,
    sessionLabel: teamDetail.settings.defaultSessionLabel,
    trackId,
    layout: getLayoutFromSchedule(blockLayout),
    autoSchedule,
    allowFlexWorkouts,
  };
};

export const editBlockInput = (
  blockId: string,
  {
    name,
    startDate,
    focuses,
    trainingMaxRatio,
    blockLayout,
    autoSchedule,
    allowFlexWorkouts,
  }: BlockForm,
) => {
  const blockFocuses = focuses.map((f, index) => ({
    id: v4(),
    focusId: f.id,
    sortOrder: index,
  }));

  const _startDate = DateTime.fromISO(startDate);
  if (!_startDate.isValid) {
    // shouldn't ever happen but is needed so endDate is the correct type
    throw new Error('Invalid start date');
  }
  return {
    id: blockId,
    name,
    focuses: blockFocuses,
    trainingMaxRatio,
    startDate,
    endDate: getBlockEndDateFromForm(startDate, blockLayout),
    layout: getLayoutFromSchedule(blockLayout),
    autoSchedule,
    allowFlexWorkouts,
  };
};

export function getLayoutFromSchedule(blockSchedule: number[]) {
  return Object.fromEntries(
    blockSchedule.map((sessionCount, index) => [index + 1, sessionCount]),
  );
}

// Track ids that have at least one block with published workouts. Soft-deleted
// blocks are tombstoned locally (sync `update` actions keep the row with
// `deletedAt` set), so they must not count — the server-side deleteTrack guard
// ignores them too. Canceled blocks keep their published workouts and still
// count; that's deliberate (SC-2365).
export function collectPublishedBlockTrackIds(
  blocks: {
    trackId: string;
    hasPublishedWorkouts: boolean;
    deletedAt?: string | null;
  }[],
): Set<string> {
  const set = new Set<string>();
  for (const block of blocks) {
    if (block.deletedAt) continue;
    if (block.hasPublishedWorkouts) set.add(block.trackId);
  }
  return set;
}

// A parent track is treated as having published blocks if any of its
// sub-tracks does, since deleting the parent cascades to its sub-tracks.
export function trackHasPublishedBlocks(
  trackId: string | undefined,
  tracks: TrackPreview[],
): boolean {
  if (!trackId) return false;
  const track = tracks.find((t) => t.id === trackId);
  if (!track) return false;
  if (track.hasPublishedBlocks) return true;
  return tracks.some(
    (t) => t.parentTrackId === trackId && t.hasPublishedBlocks,
  );
}

export function getEffectiveTrackColor(
  track: TrackRow,
  tracksById: Map<string, TrackRow>,
): string | null {
  return (
    track.color ??
    (track.parentTrackId
      ? (tracksById.get(track.parentTrackId)?.color ?? null)
      : null)
  );
}

export function isTrackDeleteDisabled(
  trackId: string | undefined,
  tracks: TrackPreview[],
  role: ProfileRoleType | undefined,
): boolean {
  return (
    trackHasPublishedBlocks(trackId, tracks) ||
    role === CRICKET_BOWLING_COACH_PROFILE_ROLE
  );
}
